'use client';

import { Alert, Button, KeyValue, Tag, Tooltip } from '@lulalife/design-system';
import { AlertTriangle, ChevronDown, ChevronUp, Tool01 } from '@lulalife/design-system/icons/line';
import { useState } from 'react';
import {
  formatAge,
  formatExpectedLife,
  formatManufactured,
  formatRegistration,
} from 'src/helper/applianceUtils';
import type { ApplianceType } from 'src/helper/shared-types';
import { DSSurface } from './DSSurface';

/** The Low, Medium and High bands the old gauge drew, as the frames' tag. */
const riskTag = (score: number) => {
  if (score <= 2) return { label: 'Low Risk', color: 'success' as const };
  if (score <= 4) return { label: 'Medium Risk', color: 'warning' as const };
  return { label: 'High Risk', color: 'danger' as const };
};

type ApplianceCardProps = {
  appliance: ApplianceType;
  defaultExpanded?: boolean;
};

/**
 * Local copy of the Figma library's `Appliance Card` (Figma 8052:120362), which npm 0.30.28 doesn't
 * ship. Swap this one component when the DS publishes it. "Mode:" is the library's copy, and detail
 * rows have no gap with alerts 12 px apart, as it draws them.
 */
export const ApplianceCard = ({ appliance, defaultExpanded = false }: ApplianceCardProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const recalls = appliance.recalls ?? [];
  const recommendation = appliance.recommendations?.message;
  const risk = appliance.risk?.score_max && appliance.risk.score != null ? appliance.risk : null;
  const expectedLife = formatExpectedLife(appliance.age);
  const age = formatAge(appliance.age);
  const details = [
    { label: 'Manufactured', value: formatManufactured(appliance.manufactured) },
    { label: 'Age', value: age ? [age, expectedLife?.label].filter(Boolean).join(' ') : null },
    { label: 'Warranty Status', value: appliance.warranty?.coverage?.label ?? null },
    { label: 'Registration status', value: formatRegistration(appliance.registration_status) },
  ].filter((detail): detail is { label: string; value: string } => Boolean(detail.value));
  const hasBody = Boolean(risk || details.length || recalls.length || recommendation);
  const open = expanded;

  return (
    <DSSurface
      className="flex w-full flex-col gap-space-16 rounded-card-radius border border-card-border bg-card-bg p-space-16 font-sans"
      data-testid="appliance-card"
    >
      <div className="flex items-center gap-space-12">
        <span className="flex size-[44px] shrink-0 items-center justify-center rounded-md bg-bg-brand-subtle text-icon-brand">
          <Tool01 aria-hidden="true" size={24} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-space-2">
          <div className="desktop-body-md-strong text-text-primary">
            {appliance.brand_name} {appliance.product_type_name}
          </div>
          <div className="desktop-body-sm text-text-secondary">
            Mode: {appliance.model_number}
            {appliance.serial_number ? ` - S/N: ${appliance.serial_number}` : ''}
          </div>
        </div>
        {/* The card's Recall icon: grey, and only while collapsed (Figma 8017:9521's GE Dishwasher). */}
        {recalls.length > 0 && !open ? (
          <Tooltip content={recalls[0]?.due_to ?? 'Hazard Recall'}>
            <span
              className="inline-flex text-icon-secondary"
              role="img"
              aria-label={recalls[0]?.due_to ?? 'Hazard Recall'}
            >
              <AlertTriangle aria-hidden="true" />
            </span>
          </Tooltip>
        ) : null}
        <Button
          type="button"
          variant="tertiary"
          iconOnly
          aria-expanded={open}
          aria-label={open ? 'Collapse appliance details' : 'Expand appliance details'}
          onClick={() => setExpanded((current) => !current)}
        >
          {open ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
        </Button>
      </div>

      {open && hasBody ? (
        <div className="flex flex-col gap-space-12">
          {risk ? (
            <div className="flex">
              <Tag color={riskTag(risk.score).color} label={riskTag(risk.score).label} />
            </div>
          ) : null}
          {details.length > 0 ? (
            <div className="flex flex-col">
              {details.map((detail) => (
                // `m-0` for Bootstrap's bottom margin on <dl>, which the horizontal DS KeyValue keeps.
                <KeyValue key={detail.label} label={detail.label} className="m-0">
                  {detail.value}
                </KeyValue>
              ))}
            </div>
          ) : null}
          {recalls.map((recall) => (
            <Alert
              key={recall.recall_number}
              type="error"
              role="status"
              title={`${recall.due_to} Hazard Recall`}
            >
              {recall.hazard} {recall.fix}
            </Alert>
          ))}
          {recommendation ? (
            <Alert type="ai" role="status" title="Recommendations">
              {recommendation}
            </Alert>
          ) : null}
        </div>
      ) : null}
    </DSSurface>
  );
};
